import React from 'react';
import {
  createStyles,
  makeStyles,
  Theme,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import { ChevronLeft } from '@material-ui/icons';
import classNames from 'classnames';
import { ChannelsPageLayoutHeaderContext } from './channelsLayoutHeaderContext';
import { useAppDispatch, useAppSelector } from '../../../hooks/useStore';
import { ChannelsListToolbar } from './ChannelsListToolbar';
import { ChannelContentToolbar } from './ChannelsContentToolbar';
import {
  togglePrimarySidebarMobile,
  toggleSecondarySidebar,
} from '../../../store/ui/actions';
import MenuLogo from '../../Navbar/MenuLogo';
import { HamburgerIcon } from '../../Icons';
import { RouteContext } from '../../../context';
import {
  EXTENSION_EMBED_PAGE,
  FILES_PAGE,
  FORMS_RESPONSE_PAGE,
  INBOX_PAGE,
  MESSAGES_PAGE,
} from '../../../constants';
import { drawerWidth } from '../../UI';
import { DividersAndCardBorders } from '../../../theme/colors';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      height: 60,
      width: '-webkit-fill-available',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    listToolbarWrapper: {
      display: 'flex',
      alignItems: 'center',
      height: '100%',
      [theme.breakpoints.up('sm')]: {
        paddingLeft: 0,
        width: drawerWidth,
        paddingRight: theme.spacing(4.5),
        // add border right only when
        // channel list toolbar component  is rendered
        borderRight: `1px solid ${theme.palette.divider}`,
        borderBottom: 0,
      },
      // on mobile screens
      // we want space between menu button, page title and actions
      // but when secondary sidebar is not open we don't want any space between
      // menu button and active channel title
      [theme.breakpoints.down('xs')]: {
        // using width:0 and minWidth:100% we are preventing
        // toolbar from expanding when search input
        // width changes
        width: 0,
        minWidth: 'fit-content',
      },
    },
    hiddenContentToolbar: {
      [theme.breakpoints.up('sm')]: {
        paddingLeft: theme.spacing(4.5),
        borderBottom: `1px solid ${DividersAndCardBorders}`,
      },
    },
    hiddenListToolbar: {
      [theme.breakpoints.up('sm')]: {
        width: 'fit-content',
        paddingRight: 0,
        borderRight: 0,
      },
    },
    secondarySidebarOpen: {
      [theme.breakpoints.down('xs')]: {
        minWidth: '100%',
      },
    },
  }),
);

/**
 * The channels page header component is composed of 2 parts:
 * 1. Channels list toolbar: contains the page title and the create button
 * 2. Channels content toolbar: contains the channel title and the channel actions
 */
export const ChannelsPageHeader: React.FC = () => {
  const { pathname } = React.useContext(RouteContext);
  const isSecondarySidebarOpen = useAppSelector(
    state => state.ui.secondarySideBar.isOpen,
  );
  const isSecondarySidebarHidden = useAppSelector(
    state => state.ui.secondarySideBar.hidden,
  );

  const {
    createButtonProps,
    activeChannelTitle,
    channelHeaderActions = [],
    setChannelHeaderMenuAnchorEl,
    channelsListToolbarHidden,
    pageTitle,
    channelsContentToolbarHidden,
  } = React.useContext(ChannelsPageLayoutHeaderContext);

  const classes = useStyles();

  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('xs'));

  const isExtensionsPage = pathname === EXTENSION_EMBED_PAGE.path;
  const isMessagesPages = pathname === MESSAGES_PAGE.path;
  const isFilesPages = pathname === FILES_PAGE.path;
  const isFormsResponsePage = pathname === FORMS_RESPONSE_PAGE.path;
  const isInboxPage = pathname === INBOX_PAGE.path;

  // show back button when secondary sidebar is not open and hidden
  // only on mobile screens
  const showBackNavigationButton =
    (isExtensionsPage ||
      isMessagesPages ||
      isFilesPages ||
      isFormsResponsePage ||
      isInboxPage) &&
    !isSecondarySidebarHidden && // this is used to decided if the sidebar gets shown at all
    !isSecondarySidebarOpen && // this is used to control the stage of the sidebar so it can be toggled
    isMobileScreen;

  /**
   * This function handles the menu button click event.
   * If the secondary sidebar is open, it should close it.
   * Otherwise, it should open the primary sidebar.
   * @returns
   */
  const handleMenuButtonClick = React.useCallback(() => {
    if (!showBackNavigationButton) {
      dispatch(
        togglePrimarySidebarMobile({
          isOpen: true,
        }),
      );
    } else {
      dispatch(
        toggleSecondarySidebar({
          isOpen: true,
        }),
      );
    }
  }, [showBackNavigationButton]);

  return (
    <div className={classes.root}>
      <div
        className={classNames(classes.listToolbarWrapper, {
          [classes.hiddenContentToolbar]:
            channelsContentToolbarHidden && !isSecondarySidebarHidden,
          [classes.hiddenListToolbar]: channelsListToolbarHidden,
          // list toolbar should be extended 100% of the available space when the secondary sidebar is open;
          // otherwise, its width should be enough to fit the content in it
          [classes.secondarySidebarOpen]: isSecondarySidebarOpen,
        })}
      >
        <MenuLogo
          menuClickCallback={handleMenuButtonClick}
          Icon={showBackNavigationButton ? ChevronLeft : HamburgerIcon}
        />
        {!channelsListToolbarHidden && (
          <ChannelsListToolbar
            pageTitle={pageTitle}
            createButtonProps={createButtonProps}
            isSecondarySidebarOpen={Boolean(isSecondarySidebarOpen)}
          />
        )}
      </div>
      {!channelsContentToolbarHidden && (
        <ChannelContentToolbar
          activeChannelTitle={activeChannelTitle}
          channelHeaderActions={channelHeaderActions}
          setChannelHeaderMenuAnchorEl={setChannelHeaderMenuAnchorEl}
          isSecondarySidebarOpen={Boolean(isSecondarySidebarOpen)}
        />
      )}
    </div>
  );
};
